import React from "react";
import { AlertTriangle } from "lucide-react";
import { ago, compact, num, usd } from "./networkFmt";

export function ScopeChip({ data, testId = "network-scope-chip" }) {
    const f = data.freshness;
    return (
        <span data-testid={testId} className="inline-flex items-center gap-1.5 text-[10px] font-mono text-white/45 border border-white/10 rounded-full px-2 py-0.5">
            <span className={`w-1.5 h-1.5 rounded-full ${f.complete ? "bg-emerald-400" : "bg-amber-400"}`} />
            {data.selection.label} · {data.selection.date_from} → {data.selection.date_to} · updated {ago(f.oldest_reported_at)}
        </span>
    );
}

function Kpi({ label, value, sub, testId, tone }) {
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 transition-colors duration-200 hover:border-white/25" data-testid={testId}>
            <div className="label-xs">{label}</div>
            <div className={`mt-1.5 font-mono text-lg ${tone || "text-white/90"}`} data-testid={`${testId}-value`}>{value}</div>
            {sub && <div className="mt-0.5 font-mono text-[10px] text-white/40">{sub}</div>}
        </div>
    );
}

function FreshnessNotice({ f }) {
    if (f.complete) return null;
    return (
        <div className="flex items-start gap-2 border border-amber-500/30 bg-amber-500/5 rounded-md p-3 text-xs text-amber-200" data-testid="network-freshness-warning">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
                Totals may be incomplete.
                {f.stale.length > 0 && <> Stale (no update for over an hour): <b>{f.stale.join(", ")}</b>.</>}
                {f.not_reporting.length > 0 && <> Not reporting: <b>{f.not_reporting.join(", ")}</b>.</>}
            </div>
        </div>
    );
}

export default function NetworkHeadline({ data, showNote = true }) {
    const t = data.totals;
    return (
        <div className="space-y-3" data-testid="network-headline">
            <ScopeChip data={data} />
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <Kpi testId="network-kpi-ai" label="AI spend" value={usd(t.ai_cost_usd)} tone="text-[#A78BFA]" sub="every AI call" />
                <Kpi testId="network-kpi-reuse" label="Reuse charges" value={usd(t.reuse_cost_usd)}
                    sub={`${num(t.reuse_docs)} docs reused${t.reuse_free ? ` · ${num(t.reuse_free)} free` : ""}`} />
                <Kpi testId="network-kpi-saved" label="AI cost avoided" value={usd(t.reuse_saved_usd)} tone="text-emerald-300" sub="reused instead of re-processed" />
                <Kpi testId="network-kpi-total" label="Total cost" value={usd(t.total_cost_usd)} tone="text-white" sub="AI spend + reuse" />
                <Kpi testId="network-kpi-calls" label="AI calls" value={num(t.calls)} />
                <Kpi testId="network-kpi-tokens" label="Tokens used" value={compact(t.tokens)} sub={`${compact(t.tokens_in)} in / ${compact(t.tokens_out)} out`} />
            </div>
            <FreshnessNotice f={data.freshness} />
            {showNote && (
                <p className="text-[11px] text-white/35" data-testid="network-scope-note">
                    AI spend counts every AI call on each server (agents, flows, ingests, study builds, Radar, summaries…) — the same ledger as the
                    cost block on each API call. The AI Usage rollup below counts only agent and flow runs on this server, so its figure can be lower.
                    Reuse lists every time stored information was served instead of re-running the AI; exempt clients (SiteCentric) are logged at $0.
                </p>
            )}
        </div>
    );
}
